import type { IPoint } from '@/modules/Editor/point'
import type { View } from '@/modules/Editor/views/view'
import { calcBBoxByType } from '@/modules/Editor/bbox/calcBBoxByType'
import { inferCurrentAnnotationData } from '@/modules/Editor/inferCurrentAnnotationData'
import { serializeFrame } from '@/modules/Editor/serialization/serializeFrame'
import { MainAnnotationType } from '@/core/annotationTypes'
import { getMaskAnnotationBBox } from '@/modules/Editor/plugins/mask/utils/shared/getMaskAnnotationBBox'
import { getOrCreateRasterForView } from '@/modules/Editor/plugins/mask/utils/shared/getOrCreateRasterForView'

/**
 * Finds all annotations that intersect with a multi-selection rectangle
 *
 * Used during CMD+drag rectangle selection to determine which annotations
 * should be added to the current selection. Only includes unlocked, visible,
 * selectable annotations that have bounding boxes intersecting the rectangle.
 *
 * @param view - The editor view containing annotations and camera
 * @param rectangleStart - Starting point of selection rectangle (canvas coordinates)
 * @param rectangleCurrent - Current point of selection rectangle (canvas coordinates)
 * @returns Set of annotation IDs that intersect with the rectangle
 */
export const findAnnotationsIntersectingMultiselectRectangle = (
  view: View,
  rectangleStart: IPoint,
  rectangleCurrent: IPoint,
): Set<string> => {
  const intersecting = new Set<string>()

  // Convert canvas rectangle to image coordinates
  const startImage = view.camera.canvasViewToImageView(rectangleStart)
  const currentImage = view.camera.canvasViewToImageView(rectangleCurrent)

  if (!startImage || !currentImage) {
    return intersecting
  }

  // Create rectangle bounds (handle negative width/height)
  const rectLeft = Math.min(startImage.x, currentImage.x)
  const rectTop = Math.min(startImage.y, currentImage.y)
  const rectRight = Math.max(startImage.x, currentImage.x)
  const rectBottom = Math.max(startImage.y, currentImage.y)

  // Check each annotation for intersection
  view.annotationManager.annotations.forEach((annotation) => {
    // Skip locked annotations
    if (view.annotationManager.isLocked(annotation.id)) {
      return
    }

    // Skip hidden annotations
    if (view.annotationManager.isHidden(annotation.id)) {
      return
    }

    // Skip annotation types that don't support bounding box calculation
    if (annotation.type === MainAnnotationType.Tag) {
      return
    }

    // Get current frame data for the annotation
    const annotationData = inferCurrentAnnotationData(annotation, view.currentFrameIndex)
    if (!annotationData || Object.keys(annotationData).length === 0) {
      return
    }

    // Convert AnnotationData to FrameData for calcBBoxByType
    const frameData = serializeFrame(annotation.type, annotationData)

    // Calculate annotation bounding box using the proper method
    const bounds =
      annotation.type === MainAnnotationType.Mask
        ? getMaskAnnotationBBox(
            getOrCreateRasterForView(view),
            annotation.id,
            view.currentFrameIndex,
          )
        : calcBBoxByType(annotation.type as MainAnnotationType, frameData)
    if (!bounds) {
      return
    }

    // Check if rectangles intersect
    const annotationLeft = bounds.x - bounds.width / 2
    const annotationTop = bounds.y - bounds.height / 2
    const annotationRight = bounds.x + bounds.width / 2
    const annotationBottom = bounds.y + bounds.height / 2

    const intersects = !(
      rectRight < annotationLeft ||
      rectLeft > annotationRight ||
      rectBottom < annotationTop ||
      rectTop > annotationBottom
    )

    if (intersects) {
      intersecting.add(annotation.id)
    }
  })

  return intersecting
}
